import { DateOnly } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency } from '@if/core/types'
import { decimalToPercent, formatCurrency } from '@if/core/utils'
import { Help, renderSelectMenuItems, useAsyncValidation } from '@if/mui'
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { TermPlanOptionsQueryInput, useCalculateTermPlanOptions } from '@src/data/api/computation/term-plan-api'
import { debounce } from 'lodash-es'
import { ReactNode, useEffect, useId, useMemo, useState } from 'react'
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useBaseWorksheetFormConfig, useWorksheetSimulationPricing } from './BaseWorksheetForm.config'
import { getInvalidTermReasonKey, WORKSHEET_INPUT_DEBOUNCE_MS } from './BaseWorksheetForm.helpers'
import { PlanValidationContext, TermValidationContext } from './BaseWorksheetForm.schema'

export interface TermPlanOption {
  key: string
  paymentPlanId: string
  termMonths: number
  planLabel?: string
  monthlyPayment: number
  promoRate: number
  regularRate: number
  promoDurationMonths?: number
  merchantFeeRate: number
  borrowerFeeRate: number
  disabled?: boolean
  reason?: string
  isBestTerm?: boolean
  isDefault?: boolean
  // Set on the row synthesized from a saved worksheet whose (plan, term) the server no longer
  // offers. It has no pricing to show, so payment and rate render empty.
  isStoredFallback?: boolean
}

export interface StoredTermAndPlan {
  paymentPlanId: string
  term: number
  label?: string
}

export interface TermPlanOptionsInput {
  paymentFrequency: EPaymentFrequency
  deliveryOn: DateOnly
  firstPaymentOn: DateOnly
  amountRequested: number
  includeInsurance: boolean
}

export interface UseTermPlanOptionsResult {
  options: TermPlanOption[]
  hasPromoRows: boolean
  isLoading: boolean
}

const isRegularRow = (o: TermPlanOption): boolean => o.planLabel == null

export const useTermPlanOptions = (
  input: TermPlanOptionsInput,
  storedTermAndPlan?: StoredTermAndPlan | null,
): UseTermPlanOptionsResult => {
  const config = useBaseWorksheetFormConfig()
  const simulationPricing = useWorksheetSimulationPricing()

  const [debouncedAmountRequested, setDebouncedAmountRequested] = useState(input.amountRequested)
  const debouncedSetAmountRequested = useMemo(
    () => debounce((value: number) => setDebouncedAmountRequested(value), WORKSHEET_INPUT_DEBOUNCE_MS),
    [],
  )
  useEffect(() => {
    debouncedSetAmountRequested(input.amountRequested)
    return () => {
      debouncedSetAmountRequested.cancel()
    }
  }, [input.amountRequested, debouncedSetAmountRequested])

  const queryInput = useMemo<TermPlanOptionsQueryInput | undefined>(() => {
    if (!debouncedAmountRequested || debouncedAmountRequested <= 0) return undefined
    if (!input.paymentFrequency || !input.deliveryOn || !input.firstPaymentOn) return undefined

    const shared = {
      paymentFrequency: input.paymentFrequency,
      deliveryOn: input.deliveryOn,
      firstPaymentOn: input.firstPaymentOn,
      amountRequested: debouncedAmountRequested,
      includeInsurance: input.includeInsurance,
      originSystemId: EOriginSystemId.Backoffice,
    }

    if (simulationPricing) {
      if (!config.merchantId) return undefined
      return {
        isSimulation: true,
        dto: {
          merchantId: config.merchantId,
          stateIso: config.stateIso,
          hasCoapplicant: config.hasCoApplicant,
          interestRate: simulationPricing.interestRate,
          maxPmtAmount: simulationPricing.maxPmtAmount,
          ...shared,
        },
      }
    }

    if (!config.creditApplicationId) return undefined
    return { isSimulation: false, dto: { creditApplicationId: config.creditApplicationId, ...shared } }
  }, [config, input, simulationPricing, debouncedAmountRequested])

  const [rows, isLoading] = useCalculateTermPlanOptions(config.financingProgramId, queryInput)

  const options = useMemo<TermPlanOption[]>(() => {
    const mapped = (rows ?? []).map<TermPlanOption>((r) => ({
      key: `${r.paymentPlanId}|${r.term}`,
      paymentPlanId: r.paymentPlanId,
      termMonths: r.term,
      planLabel: r.planLabel ?? undefined,
      monthlyPayment: r.monthlyPayment,
      promoRate: decimalToPercent(r.promoRate),
      regularRate: decimalToPercent(r.regularRate),
      promoDurationMonths: r.promoDurationMonths ?? undefined,
      merchantFeeRate: r.merchantFeeRate,
      borrowerFeeRate: r.borrowerFeeRate,
      disabled: r.status === 'invalid',
      reason: r.reason ?? undefined,
      isBestTerm: r.isBestTerm,
      isDefault: r.isDefault,
    }))
    mapped.sort((a, b) => a.termMonths - b.termMonths || Number(isRegularRow(a)) - Number(isRegularRow(b)))

    if (!storedTermAndPlan) return mapped
    const storedKey = `${storedTermAndPlan.paymentPlanId}|${storedTermAndPlan.term}`
    if (mapped.some((option) => option.key === storedKey)) return mapped

    return [
      ...mapped,
      {
        key: storedKey,
        paymentPlanId: storedTermAndPlan.paymentPlanId,
        termMonths: storedTermAndPlan.term,
        planLabel: storedTermAndPlan.label,
        monthlyPayment: 0,
        promoRate: 0,
        regularRate: 0,
        merchantFeeRate: 0,
        borrowerFeeRate: 0,
        disabled: true,
        reason: 'storedUnavailable',
        isStoredFallback: true,
      },
    ]
  }, [rows, storedTermAndPlan])

  const hasPromoRows = useMemo(() => options.some((o) => o.planLabel != null), [options])

  return { options, hasPromoRows, isLoading }
}

const RateCell = ({ option }: { option: TermPlanOption }): ReactNode => (
  <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'right' }} noWrap>
    {option.isStoredFallback ? '' : `${option.promoRate}%`}
  </Typography>
)

const OptionRow = ({ option, hasPromoRows }: { option: TermPlanOption; hasPromoRows: boolean }): ReactNode => {
  const { t } = useTranslation()
  const showBestDot = !!option.isBestTerm && !option.disabled
  const detail = option.promoDurationMonths
    ? t('worksheetCommon.promoRateDetail', {
        promoRate: option.promoRate,
        regularRate: option.regularRate,
        count: option.promoDurationMonths,
      })
    : undefined
  const reasonKey = option.disabled ? getInvalidTermReasonKey(option.reason) : undefined

  const row = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
      <ListItemText
        sx={{ flex: 1, my: 0 }}
        primary={t('worksheetCommon.termMonths', { count: option.termMonths })}
        secondary={
          hasPromoRows ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {option.planLabel ?? t('worksheetCommon.regularPlan')}
              {detail && (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'baseline' }}
                  data-testid="term-and-plan-rate-help"
                >
                  <Help title={detail} />
                </Box>
              )}
            </Box>
          ) : undefined
        }
      />
      <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'right' }} noWrap>
        {option.disabled ? '' : formatCurrency(option.monthlyPayment)}
      </Typography>
      <RateCell option={option} />
      <Box sx={{ minWidth: 64, display: 'flex', justifyContent: 'center' }}>
        {option.isDefault && (
          <Chip
            size="small"
            label={t('worksheetCommon.termAndPlanDefault')}
            data-testid="term-and-plan-default-badge"
          />
        )}
      </Box>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: showBestDot ? 'success.main' : 'transparent',
        }}
        data-testid={showBestDot ? 'term-and-plan-best-dot' : undefined}
      />
    </Box>
  )

  if (!reasonKey) return row
  return (
    <Tooltip title={t(reasonKey)} placement="right">
      <Box component="span" sx={{ display: 'flex', width: '100%', pointerEvents: 'auto' }}>
        {row}
      </Box>
    </Tooltip>
  )
}

interface TermPlanSelectProps<T extends FieldValues & { paymentPlanId: string | null; term: number | null }>
  extends TermPlanOptionsInput {
  control: Control<T>
  label?: string
  disabled?: boolean
  storedTermAndPlan?: StoredTermAndPlan | null
}

const TermPlanSelect = <T extends FieldValues & { paymentPlanId: string | null; term: number | null }>({
  control,
  label,
  disabled,
  storedTermAndPlan,
  ...optionsInput
}: TermPlanSelectProps<T>) => {
  const { t } = useTranslation()
  const fieldLabel = label ?? t('worksheetCommon.termAndPlan')
  const labelId = useId()

  const { field: planField } = useController({ control, name: 'paymentPlanId' as FieldPathByValue<T, string> })
  const { field: termField, fieldState: termFieldState } = useController({
    control,
    name: 'term' as FieldPathByValue<T, number>,
  })

  const { options, hasPromoRows, isLoading } = useTermPlanOptions(optionsInput, storedTermAndPlan)

  const termData = useMemo<TermValidationContext | null>(() => {
    if (isLoading || options.length === 0) return null
    const validTerms = new Set(options.filter((o) => !o.disabled).map((o) => o.termMonths))
    return { validTerms }
  }, [isLoading, options])

  const planData = useMemo<PlanValidationContext | null>(() => {
    if (isLoading || options.length === 0) return null
    return { validPlanIds: new Set(options.filter((o) => !o.disabled).map((o) => o.paymentPlanId)) }
  }, [isLoading, options])

  useAsyncValidation('term', { loading: isLoading, data: termData })
  useAsyncValidation('paymentPlanId', { loading: isLoading, data: planData })

  const currentKey = termField.value != null ? `${planField.value}|${termField.value}` : ''
  const value = options.some((o) => o.key === currentKey) ? currentKey : ''

  const menuItems = useMemo(
    () =>
      renderSelectMenuItems(options, (o) => ({
        value: o.key,
        label: <OptionRow option={o} hasPromoRows={hasPromoRows} />,
        disabled: o.disabled,
      })),
    [options, hasPromoRows],
  )

  const handleChange = (event: SelectChangeEvent<string>) => {
    const option = options.find((o) => o.key === event.target.value)
    if (!option || option.disabled) return
    planField.onChange(option.paymentPlanId)
    termField.onChange(option.termMonths)
  }

  const renderValue = (selected: string): ReactNode => {
    const option = options.find((o) => o.key === selected)
    if (!option) return null
    const subtitle = hasPromoRows ? ` · ${option.planLabel ?? t('worksheetCommon.regularPlan')}` : ''
    return `${t('worksheetCommon.termMonths', { count: option.termMonths })}${subtitle}`
  }

  if (isLoading) {
    return <Skeleton variant="rounded" height={56} data-testid="term-and-plan-loading" />
  }

  return (
    <FormControl fullWidth error={!!termFieldState.error}>
      <InputLabel id={labelId}>{fieldLabel}</InputLabel>
      <Select
        labelId={labelId}
        label={fieldLabel}
        value={value}
        onChange={handleChange}
        onBlur={termField.onBlur}
        disabled={disabled}
        renderValue={renderValue}
      >
        {menuItems.length > 0 ? menuItems : <MenuItem value="" disabled />}
      </Select>
      {termFieldState.error?.message && <FormHelperText>{termFieldState.error.message}</FormHelperText>}
    </FormControl>
  )
}

export default TermPlanSelect
